<template>
  <div class="merchant-management-page">
    <!-- 页面头部 -->
    <page-header>
      <el-button type="primary" @click="addMerchant">新增商户</el-button>
    </page-header>

    <!-- 搜索和筛选区域 -->
    <div class="filter-area">
      <el-card class="filter-card">
        <el-form :model="filterForm" ref="filterFormRef" inline label-width="80px">
          <el-form-item label="商户名称" prop="name">
            <el-input
                v-model="filterForm.name"
                placeholder="请输入商户名称"
                clearable
                style="width: 200px"
                @keyup.enter.native="handleSearch"
            />
          </el-form-item>

          <el-form-item label="行业分类" prop="industryCategoryId">
            <el-select
                v-model="filterForm.industryCategoryId"
                placeholder="请选择行业"
                clearable
                filterable
                style="width: 200px"
                @change="handleFilterChange"
            >
              <el-option
                  v-for="industry in filterIndustryOptions"
                  :key="industry.id"
                  :label="industry.name"
                  :value="industry.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="营业状态" prop="status">
            <el-select
                v-model="filterForm.status"
                placeholder="请选择状态"
                clearable
                style="width: 120px"
                @change="handleFilterChange"
            >
              <el-option label="正常营业" :value="1" />
              <el-option label="暂停营业" :value="2" />
              <el-option label="永久关闭" :value="3" />
            </el-select>
          </el-form-item>

          <el-form-item label="排序方式" prop="sortField">
            <el-select
                v-model="filterForm.sortField"
                placeholder="请选择排序字段"
                clearable
                style="width: 150px"
                @change="handleSortChange"
            >
              <el-option label="注册时间" value="addDataTime" />
              <el-option label="更新时间" value="lastUpdateTime" />
              <el-option label="返利比例" value="rebateRatio" />
              <el-option label="获取返利" value="totalRebate" />
            </el-select>
          </el-form-item>

          <el-form-item label="排序方向" prop="sortOrder">
            <el-select
                v-model="filterForm.sortOrder"
                placeholder="排序方向"
                clearable
                style="width: 120px"
                @change="handleSortChange"
            >
              <el-option label="升序" value="asc" />
              <el-option label="降序" value="desc" />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              <i class="el-icon-search"></i>搜索
            </el-button>
            <el-button @click="resetFilter">
              <i class="el-icon-refresh"></i>重置
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 商户列表 -->
    <table-union-pager
        :data="tableData"
        @current-change="handlePageChange"
    >
      <el-table-column label="ID" property="id" width="80" align="center"/>

      <el-table-column label="名称" property="name" min-width="150" sortable="custom" @sort-change="handleTableSort">
        <template #default="scope">
          <div class="merchant-name-cell">
            <span>{{ scope.row.name }}</span>
            <el-tag v-if="scope.row.status === 2" size="mini" type="warning" style="margin-left: 8px;">暂停</el-tag>
            <el-tag v-if="scope.row.status === 3" size="mini" type="danger" style="margin-left: 8px;">关闭</el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="注册时间" min-width="180" sortable="custom" @sort-change="handleTableSort">
        <template #default="scope">
          {{ formatTime(scope.row.addDataTime) }}
        </template>
      </el-table-column>

      <el-table-column label="更新时间" min-width="180" sortable="custom" @sort-change="handleTableSort">
        <template #default="scope">
          {{ formatTime(scope.row.lastUpdateTime) }}
        </template>
      </el-table-column>

      <el-table-column label="行业" min-width="120">
        <template #default="scope">
          {{ getIndustryName(scope.row.industryId) || '--' }}
        </template>
      </el-table-column>

      <el-table-column label="返利比例" min-width="100" sortable="custom" @sort-change="handleTableSort">
        <template #default="scope">
          <span :class="{'highlight-value': scope.row.rebateRatio > 0}">
            {{ scope.row.rebateRatio ? scope.row.rebateRatio + '%' : '--' }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="银联商户号" min-width="180">
        <template #default="scope">
          {{ scope.row.unionPayMerchantNo || '--' }}
        </template>
      </el-table-column>

      <el-table-column label="获取返利总额" min-width="120" sortable="custom" @sort-change="handleTableSort">
        <template #default="scope">
          <div :class="{'highlight-value': scope.row.totalRebate > 0}">
            {{ scope.row.totalRebate ? scope.row.totalRebate + '元' : '--' }}
          </div>
        </template>
      </el-table-column>

      <el-table-column label="操作" width="200" fixed="right">
        <template #default="scope">
          <el-button type="text" @click="showDetail(scope.row)">详情</el-button>
          <el-button type="text" @click="toClerks(scope.row)">员工列表</el-button>
          <el-button type="text" @click="profileEdit(scope.row)">编辑店铺</el-button>
          <el-button type="text" @click="delProfile(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </table-union-pager>

    <!-- 商户详情弹窗 -->
    <el-dialog
        :visible.sync="detailDialog.visible"
        title="商户详情"
        append-to-body
        :close-on-click-modal="false"
        width="900px"
        top="20px"
        custom-class="merchant-detail-dialog"
    >
      <el-form label-width="120px" :model="detailDialog.detail">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="名称">
              <el-input v-model="detailDialog.detail.name" disabled/>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="注册手机号">
              <span>{{ detailDialog.detail.mainClerkPhone || '--' }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="员工数量">
              <span>{{ detailDialog.detail.clerkCount || 0 }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="发展会员数量">
              <span>{{ detailDialog.detail.userCount || 0 }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="行业">
              <el-input v-model="detailDialog.detail.industry" disabled/>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="返利比例">
              <el-input :value="detailDialog.detail.rebateRatio" disabled>
                <template #append>%</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="已获返利">
              <span>{{ detailDialog.detail.rebateAmount || 0 }}元</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="银联商户号">
              <span>{{ detailDialog.detail.unionPayAccount || '--' }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-form-item label="图片资料">
            <div class="merchant-image-infos">
              <div
                  class="merchant-image-info"
                  v-for="(imageInfo,index) of detailDialog.detail.imageInfoList"
                  :key="imageInfo.id || index"
              >
                <image-placeholder
                    zoom-on-click
                    :image="imageInfo"
                    :show-delete="true"
                    @delete="deleteMerchantImageInfo(index)"
                />
                <div class="merchant-image-info-name">{{ imageInfo.name }}</div>
              </div>
              <div class="merchant-image-info">
                <image-placeholder @click="addMerchantImageInfo"/>
                <div class="merchant-image-info-name"></div>
              </div>
            </div>
          </el-form-item>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialog.visible = false">关闭</el-button>
        <el-button type="primary" @click="editMerchant">保存修改</el-button>
      </div>
    </el-dialog>

    <!-- 新增商户弹窗 -->
    <el-dialog
        :visible.sync="addMerchantDialog.visible"
        title="新增商户"
        append-to-body
        :close-on-click-modal="false"
        top="20px"
        width="600px"
        custom-class="add-merchant-dialog"
    >
      <el-form
          :model="addMerchantDialog.form"
          :rules="addMerchantRules"
          ref="addMerchantForm"
          label-width="100px"
      >
        <el-form-item label="名称" prop="name">
          <el-input v-model="addMerchantDialog.form.name" placeholder="请输入商户名称"/>
        </el-form-item>
        <el-form-item label="分类" prop="categoryId">
          <el-select
              v-model="addMerchantDialog.form.categoryId"
              placeholder="请选择分类"
              filterable
          >
            <el-option
                v-for="category of categories"
                :key="category.id"
                :label="category.name"
                :value="category.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="行业" prop="industry">
          <el-input v-model="addMerchantDialog.form.industry" placeholder="请输入所属行业"/>
        </el-form-item>
        <el-form-item label="展示商品数" prop="productCount">
          <el-input-number
              v-model="addMerchantDialog.form.productCount"
              :min="0"
              :max="999"
              placeholder="请输入展示商品数量"
          />
        </el-form-item>
        <el-form-item label="图片" prop="image">
          <image-placeholder
              @click="selectImage('add')"
              :image="addMerchantDialog.form.image"
          />
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input v-model="addMerchantDialog.form.address" placeholder="请输入详细地址"/>
        </el-form-item>
        <el-form-item label="简介" prop="introduction">
          <el-input
              type="textarea"
              :autosize="{minRows:4,maxRows:4}"
              resize="none"
              v-model="addMerchantDialog.form.introduction"
              placeholder="请输入商户简介"
          />
        </el-form-item>
        <el-form-item label="返利比例" prop="rebateRatio">
          <el-input
              v-model="addMerchantDialog.form.rebateRatio"
              placeholder="请输入返利比例"
          >
            <template #append>%</template>
          </el-input>
        </el-form-item>
        <el-form-item label="联系电话" prop="phones">
          <div>
            <el-button
                circle
                size="mini"
                icon="el-icon-plus"
                @click="addMerchantDialog.form.phones.push('')"
            />
          </div>
          <div
              v-for="(phone,index) of addMerchantDialog.form.phones"
              :key="index"
              style="margin-top: 8px;"
          >
            <el-input
                v-model="addMerchantDialog.form.phones[index]"
                placeholder="请输入联系电话"
            >
              <template #append>
                <el-button
                    circle
                    size="mini"
                    icon="el-icon-delete"
                    type="danger"
                    @click="addMerchantDialog.form.phones.splice(index,1)"
                />
              </template>
            </el-input>
          </div>
        </el-form-item>
        <el-form-item label="登录手机号" prop="mainClerkPhone">
          <el-input
              v-model="addMerchantDialog.form.mainClerkPhone"
              placeholder="请输入管理员登录手机号"
          />
        </el-form-item>
        <el-form-item label="标签">
          <el-tag
              v-for="(tag,index) of addMerchantDialog.form.tags"
              :key="index"
              closable
              @close="addMerchantDialog.form.tags.splice(index,1)"
          >
            {{ tag || '请输入标签' }}
          </el-tag>
          <el-button
              circle
              size="mini"
              icon="el-icon-plus"
              @click="addMerchantDialog.form.tags.push('')"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addMerchantDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="confirmAddMerchant">提交</el-button>
      </div>
    </el-dialog>

    <!-- 图片上传弹窗 -->
    <el-dialog
        title="上传图片资料"
        :visible.sync="imageInfoUploadDialog.visible"
        append-to-body
        :close-on-click-modal="false"
        top="40px"
        width="500px"
        custom-class="image-upload-dialog"
    >
      <input
          ref="imageInfoInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="selectMerchantInfoImageCallback"
      />
      <div style="display: flex;align-items: center;flex-direction: column">
        <image-placeholder
            @click="$refs['imageInfoInput'].click()"
            style="margin-bottom: 20px"
            :image="imageInfoUploadDialog.preview ? {url:imageInfoUploadDialog.preview} : null"
        />
        <el-form label-width="80px">
          <el-form-item label="名称">
            <el-input v-model="imageInfoUploadDialog.name" placeholder="请输入图片名称"/>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="imageInfoUploadDialog.visible = false;">取消</el-button>
        <el-button
            type="primary"
            @click="confirmUploadImageInfo"
            :disabled="!imageInfoUploadDialog.file"
        >
          上传
        </el-button>
      </div>
    </el-dialog>

    <!-- 编辑店铺弹窗 -->
    <el-dialog
        :visible.sync="storeDialogVisible"
        title="编辑店铺资料"
        width="80%"
        top="5vh"
        modal
        :modal-append-to-body="false"
        :append-to-body="true"
        :destroy-on-close="true"
        custom-class="store-edit-dialog"
    >
      <div v-loading="storeLoading">
        <el-form
            :model="storeForm"
            :rules="storeRules"
            ref="storeFormRef"
            label-width="120px"
            class="store-edit-form"
        >
          <!-- 基础信息 -->
          <div class="form-section">
            <h3 class="section-title">
              <i class="el-icon-s-shop"></i> 基础信息
            </h3>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="店铺名称" prop="name">
                  <el-input
                      v-model="storeForm.name"
                      placeholder="请输入店铺名称"
                      maxlength="50"
                      show-word-limit
                      clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="营业状态" prop="status">
                  <el-radio-group v-model="storeForm.status">
                    <el-radio :label="1">正常营业</el-radio>
                    <el-radio :label="2">暂停营业</el-radio>
                    <el-radio :label="3">永久关闭</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 三级分类选择 -->
            <el-row :gutter="20" class="category-row">
              <el-col :span="8">
                <el-form-item label="行业分类" prop="industryCategoryId">
                  <el-select
                      v-model="storeForm.industryCategoryId"
                      placeholder="请选择行业分类"
                      filterable
                      clearable
                      @change="handleIndustryChange"
                      :loading="storeCategoryLoading"
                  >
                    <el-option
                        v-for="industry in storeIndustryOptions"
                        :key="industry.id"
                        :label="industry.name"
                        :value="industry.id"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="店铺分类" prop="storeCategoryId">
                  <el-select
                      v-model="storeForm.storeCategoryId"
                      placeholder="请先选择行业分类"
                      filterable
                      clearable
                      @change="handleCategoryChange"
                      :disabled="!storeForm.industryCategoryId"
                      :loading="storeCategoryLoading"
                  >
                    <el-option
                        v-for="category in storeCategoryOptions"
                        :key="category.id"
                        :label="category.name"
                        :value="category.id"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="品类细分" prop="productCategoryId">
                  <el-select
                      v-model="storeForm.productCategoryId"
                      placeholder="请先选择店铺分类"
                      filterable
                      clearable
                      :disabled="!storeForm.storeCategoryId"
                  >
                    <el-option
                        v-for="product in storeProductCategoryOptions"
                        :key="product.id"
                        :label="product.name"
                        :value="product.id"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="店铺主图" prop="imageId">
                  <div class="image-upload-container">
                    <div
                        class="image-preview"
                        @click="openImageSelector('store')"
                    >
                      <el-image
                          v-if="storeMainImageUrl"
                          :src="storeMainImageUrl"
                          fit="cover"
                          :preview-src-list="storeMainImageUrl ? [storeMainImageUrl] : []"
                      />
                      <div v-else class="image-placeholder-text">
                        <i class="el-icon-plus"></i>
                        <span>点击上传图片</span>
                      </div>
                    </div>
                    <div class="image-actions">
                      <el-button
                          size="mini"
                          type="text"
                          @click="openImageSelector('store')"
                      >
                        {{ storeForm.imageId ? '更换图片' : '选择图片' }}
                      </el-button>
                      <el-button
                          size="mini"
                          type="text"
                          @click="removeStoreMainImage"
                          v-if="storeForm.imageId"
                      >
                        删除
                      </el-button>
                    </div>
                  </div>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="营业时间" prop="businessHours">
                  <el-input
                      v-model="storeForm.businessHours"
                      placeholder="例如：09:00-22:00"
                      clearable
                  />
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="联系方式类型" prop="contactType">
                  <el-radio-group v-model="storeForm.contactType">
                    <el-radio :label="1">手机号</el-radio>
                    <el-radio :label="2">座机号</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="联系方式" prop="contactPhone">
                  <el-input
                      v-model="storeForm.contactPhone"
                      :placeholder="storeForm.contactType === 1 ? '请输入手机号' : '请输入座机号，格式：010-12345678'"
                      @blur="validateContactPhone"
                  />
                  <div v-if="contactPhoneError" class="error-tip">{{ contactPhoneError }}</div>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="人均价格" prop="avgPrice">
                  <el-input-number
                      v-model="storeForm.avgPrice"
                      :min="0"
                      :max="99999"
                      :precision="2"
                      placeholder="请输入人均价格"
                      style="width: 100%"
                  >
                    <template #append>元</template>
                  </el-input-number>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="详细地址" prop="address">
                  <el-input
                      v-model="storeForm.address"
                      placeholder="请输入详细地址"
                      clearable
                  />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 位置选择区域 -->
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="经纬度">
                  <el-input
                      v-model="storeLocationText"
                      placeholder="请选择位置"
                      readonly
                  >
                    <template #append>
                      <el-button
                          type="text"
                          @click="openLocationSelector"
                          size="mini"
                      >
                        {{ storeForm.longitude && storeForm.latitude ? '重新选择' : '选择位置' }}
                      </el-button>
                    </template>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 坐标信息展示 -->
            <el-row :gutter="20" v-if="storeForm.longitude && storeForm.latitude">
              <el-col :span="24">
                <el-form-item label="坐标信息">
                  <div class="coordinate-info">
                    <div class="coordinate-item">
                      <span class="coordinate-label">高德地图坐标:</span>
                      <span class="coordinate-value">
                        {{ storeForm.longitude.toFixed(6) }}, {{ storeForm.latitude.toFixed(6) }}
                      </span>
                    </div>
                    <div class="coordinate-item">
                      <span class="coordinate-label">腾讯地图坐标:</span>
                      <span class="coordinate-value">
                        {{ tencentCoordinates.lng.toFixed(6) }}, {{ tencentCoordinates.lat.toFixed(6) }}
                      </span>
                      <el-button
                          type="text"
                          size="mini"
                          @click="openTencentMap"
                          style="margin-left: 10px;"
                      >
                        在腾讯地图中查看
                      </el-button>
                    </div>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 店铺介绍 -->
          <div class="form-section">
            <h3 class="section-title">
              <i class="el-icon-document"></i> 店铺介绍
            </h3>

            <el-form-item label="店铺简介" prop="introduction">
              <el-input
                  v-model="storeForm.introduction"
                  type="textarea"
                  :rows="4"
                  placeholder="请输入店铺简介"
                  maxlength="2000"
                  show-word-limit
              />
            </el-form-item>

            <el-form-item label="店铺公告" prop="notice">
              <el-input
                  v-model="storeForm.notice"
                  type="textarea"
                  :rows="3"
                  placeholder="请输入店铺公告"
                  maxlength="500"
                  show-word-limit
              />
            </el-form-item>
          </div>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="storeDialogVisible = false">取消</el-button>
        <el-button
            type="primary"
            @click="handleStoreSubmit"
            :loading="storeSubmitting"
        >
          保存修改
        </el-button>
      </div>
    </el-dialog>

    <!-- 位置选择器弹窗 -->
    <location-selector
        @select="handleLocationSelect"
        v-model="locationSelectorVisible"
        :value="currentLocation"
        custom-class="location-selector-dialog"
    />

    <!-- 公共组件 -->
    <image-selector
        v-model="imageSelector.visible"
        :max-count="imageSelector.maxCount"
        @select="selectImageCallback"
        custom-class="image-selector-dialog"
    />
    <image-selector
        v-model="storeImageSelector.visible"
        :max-count="storeImageSelector.maxCount"
        @select="handleStoreImageSelect"
        custom-class="image-selector-dialog"
    />
  </div>
</template>

<script>
// 导入公共组件
import TableUnionPager from "@/components/TableUnionPager";
import ButtonLine from "@/components/ButtonLine";
import PageHeader from "@/components/PageHeader";
import LocationSelector from "@/components/LocationSelector";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import ImageSelector from "@/components/ImageSelector";

// 导入API和工具函数
import api from "@/utils/api";
import { fileMd5 } from "@/utils/md5";

// 坐标转换工具
const transformCoordinate = {
  gcj02ToWgs84(lng, lat) {
    const PI = 3.14159265358979324;
    const a = 6378245.0;
    const ee = 0.00669342162296594323;

    let dLat = this.transformLat(lng - 105.0, lat - 35.0);
    let dLng = this.transformLng(lng - 105.0, lat - 35.0);

    const radLat = lat / 180.0 * PI;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;

    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * PI);
    dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * PI);

    const mgLat = lat + dLat;
    const mgLng = lng + dLng;

    return {
      lng: lng * 2 - mgLng,
      lat: lat * 2 - mgLat
    };
  },

  transformLat(x, y) {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
    return ret;
  },

  transformLng(x, y) {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
    return ret;
  }
};

export default {
  name: "Merchants",
  components: {
    TableUnionPager,
    ButtonLine,
    PageHeader,
    LocationSelector,
    ImagePlaceholder,
    ImageSelector
  },
  data() {
    return {
      tableData: {
        list: [],
        totalCount: 0,
        page: 1,
        pageSize: 10,
        loading: false
      },

      filterForm: {
        name: '',
        industryCategoryId: null,
        status: null,
        sortField: 'addDataTime',
        sortOrder: 'desc'
      },

      filterIndustryOptions: [],

      detailDialog: {
        visible: false,
        detail: {
          imageInfoList: []
        }
      },

      addMerchantDialog: {
        visible: false,
        form: {
          name: '',
          productCount: 10,
          image: null,
          address: '',
          longitude: null,
          latitude: null,
          introduction: '',
          rebateRatio: '',
          phones: [],
          mainClerkPhone: '',
          tags: [],
          industry: '',
          categoryId: null
        }
      },

      addMerchantRules: {
        name: [
          { required: true, message: '请输入商户名称', trigger: 'blur' },
          { min: 2, max: 50, message: '名称长度在2-50个字符之间', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        industry: [
          { required: true, message: '请输入所属行业', trigger: 'blur' }
        ],
        mainClerkPhone: [
          { required: true, message: '请输入登录手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ]
      },

      categories: [],

      imageSelector: {
        visible: false,
        mode: 'add',
        maxCount: 1
      },

      imageInfoUploadDialog: {
        visible: false,
        file: null,
        preview: '',
        name: ''
      },

      storeDialogVisible: false,
      storeLoading: false,
      storeSubmitting: false,
      storeForm: {
        id: null,
        name: '',
        imageId: null,
        status: 1,
        industryCategoryId: null,
        storeCategoryId: null,
        productCategoryId: null,
        businessHours: '',
        contactType: 1,
        contactPhone: '',
        avgPrice: 0,
        introduction: '',
        notice: '',
        address: '',
        longitude: null,
        latitude: null
      },
      storeMainImageUrl: '',
      storeIndustryOptions: [],
      storeCategoryOptions: [],
      storeProductCategoryOptions: [],
      storeCategoryLoading: false,
      storeCategoryTree: [],
      contactPhoneError: '',
      storeImageSelector: {
        visible: false,
        maxCount: 1
      },

      locationSelectorVisible: false,
      currentLocation: null,

      storeRules: {
        name: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' },
          { min: 2, max: 50, message: '名称长度在2-50个字符之间', trigger: 'blur' }
        ],
        industryCategoryId: [
          { required: true, message: '请选择行业分类', trigger: 'change' }
        ],
        storeCategoryId: [
          { required: true, message: '请选择店铺分类', trigger: 'change' }
        ],
        status: [
          { required: true, message: '请选择营业状态', trigger: 'change' }
        ],
        contactType: [
          { required: true, message: '请选择联系方式类型', trigger: 'change' }
        ],
        contactPhone: [
          { required: true, message: '请输入联系方式', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入详细地址', trigger: 'blur' }
        ],
        imageId: [
          { required: true, message: '请上传店铺主图', trigger: 'change' }
        ]
      }
    };
  },
  computed: {
    storeLocationText() {
      if (this.storeForm.longitude && this.storeForm.latitude) {
        const tencentCoords = this.convertToTencentCoordinates(
            this.storeForm.longitude,
            this.storeForm.latitude
        );
        return `${tencentCoords.lng.toFixed(6)}, ${tencentCoords.lat.toFixed(6)}`;
      }
      return '';
    },

    tencentCoordinates() {
      if (this.storeForm.longitude && this.storeForm.latitude) {
        return this.convertToTencentCoordinates(
            this.storeForm.longitude,
            this.storeForm.latitude
        );
      }
      return null;
    }
  },
  watch: {
    storeDialogVisible(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixDialogZIndex();
          }, 100);
        });
      }
    },
    'storeImageSelector.visible'(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixDialogZIndex();
          }, 50);
        });
      }
    },
    'imageSelector.visible'(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixDialogZIndex();
          }, 50);
        });
      }
    },
    locationSelectorVisible(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixDialogZIndex();
            this.ensureLocationSelectorOnTop();
          }, 50);
        });
      }
    }
  },
  created() {
    this.getMerchantList(1);
    this.getMerchantCategories();
    this.loadFilterIndustries();
  },
  mounted() {
    this.$nextTick(() => {
      this.setupDialogObserver();
    });
  },
  methods: {
    ensureLocationSelectorOnTop() {
      this.$nextTick(() => {
        const locationDialogs = document.querySelectorAll('.location-selector-dialog');
        locationDialogs.forEach(dialog => {
          if (dialog.style.display !== 'none') {
            const wrapper = dialog.closest('.el-dialog__wrapper');
            if (wrapper) {
              wrapper.style.zIndex = '4000';
              const dialogElement = wrapper.querySelector('.el-dialog');
              if (dialogElement) {
                dialogElement.style.zIndex = '4001';
              }
            }
          }
        });
      });
    },

    fixDialogZIndex() {
      this.$nextTick(() => {
        const dialogs = document.querySelectorAll('.el-dialog__wrapper:not([style*="display: none"])');

        // 首先修复遮罩层
        const modals = document.querySelectorAll('.v-modal');
        modals.forEach((modal, index) => {
          // 遮罩层应该比对应的弹窗低1
          modal.style.zIndex = (2000 + index * 1000 - 1).toString();
        });

        // 按类型分配弹窗的z-index
        dialogs.forEach((dialog, index) => {
          const dialogElement = dialog.querySelector('.el-dialog');
          if (!dialogElement) return;

          const dialogClass = dialogElement.getAttribute('class') || '';

          // 地图选择器最高优先级
          if (dialogClass.includes('location-selector-dialog')) {
            dialog.style.zIndex = '4000';
            dialogElement.style.zIndex = '4001';
          }
          // 图片选择器第二优先级
          else if (dialogClass.includes('image-selector-dialog')) {
            dialog.style.zIndex = '3000';
            dialogElement.style.zIndex = '3001';
          }
          // 店铺编辑弹窗第三优先级
          else if (dialogClass.includes('store-edit-dialog')) {
            dialog.style.zIndex = '2000';
            dialogElement.style.zIndex = '2001';
          }
          // 其他弹窗
          else {
            dialog.style.zIndex = '1000';
            dialogElement.style.zIndex = '1001';
          }
        });
      });
    },

    setupDialogObserver() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            setTimeout(() => {
              this.fixDialogZIndex();
            }, 10);
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.$once('hook:beforeDestroy', () => {
        observer.disconnect();
      });
    },

    convertToTencentCoordinates(lng, lat) {
      try {
        return transformCoordinate.gcj02ToWgs84(lng, lat);
      } catch (error) {
        console.error('坐标转换失败:', error);
        return { lng, lat };
      }
    },

    openTencentMap() {
      if (this.tencentCoordinates) {
        const { lng, lat } = this.tencentCoordinates;
        const address = encodeURIComponent(this.storeForm.address || this.storeForm.name || '');
        const title = encodeURIComponent(this.storeForm.name || '店铺位置');

        const url = `https://map.qq.com/m/place/info/coord=${lat},${lng}&name=${title}&addr=${address}&ref=myapp`;

        window.open(url, '_blank');
      } else {
        this.$message.warning('暂无坐标信息');
      }
    },

    openImageSelector(type) {
      if (type === 'store') {
        this.storeImageSelector.visible = false;

        new Promise(resolve => {
          this.$nextTick(() => {
            resolve();
          });
        }).then(() => {
          setTimeout(() => {
            this.storeImageSelector.visible = true;
            this.storeImageSelector.maxCount = 1;

            this.$nextTick(() => {
              setTimeout(() => {
                this.fixDialogZIndex();
              }, 30);
            });
          }, 50);
        });
      }
    },

    openLocationSelector() {
      this.currentLocation = this.storeForm.longitude && this.storeForm.latitude
          ? [this.storeForm.longitude, this.storeForm.latitude]
          : null;

      this.locationSelectorVisible = false;
      this.$nextTick(() => {
        setTimeout(() => {
          this.locationSelectorVisible = true;

          this.$nextTick(() => {
            setTimeout(() => {
              this.fixDialogZIndex();
              this.ensureLocationSelectorOnTop();
            }, 50);
          });
        }, 50);
      });
    },

    handleLocationSelect(location) {
      console.log('原始位置数据:', location);

      this.storeForm.longitude = location.longitude;
      this.storeForm.latitude = location.latitude;

      const tencentCoords = this.convertToTencentCoordinates(
          location.longitude,
          location.latitude
      );

      console.log('高德坐标:', location.longitude, location.latitude);
      console.log('腾讯坐标:', tencentCoords.lng, tencentCoords.lat);

      this.$message.success(`位置已选择\n高德坐标: ${location.longitude.toFixed(6)}, ${location.latitude.toFixed(6)}\n腾讯坐标: ${tencentCoords.lng.toFixed(6)}, ${tencentCoords.lat.toFixed(6)}`);

      this.locationSelectorVisible = false;
    },

    formatTime(timestamp) {
      if (!timestamp) return '--';
      const date = new Date(timestamp);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    getIndustryName(industryId) {
      if (!industryId) return '';
      for (const category of this.storeCategoryTree) {
        if (category.id === industryId) {
          return category.name;
        }
        if (category.children) {
          for (const child of category.children) {
            if (child.id === industryId) {
              return child.name;
            }
            if (child.children) {
              for (const grandchild of child.children) {
                if (grandchild.id === industryId) {
                  return grandchild.name;
                }
              }
            }
          }
        }
      }
      return '';
    },

    handlePageChange(page) {
      console.log('分页变化事件:', page);
      if (!page || page < 1) return;
      this.tableData.page = page;
      this.getMerchantList(page);
    },

    loadFilterIndustries() {
      api.merchantCategory_getMerchantCategories({
        countMerchant: false
      }).then(res => {
        if (res.data.code === '000') {
          this.filterIndustryOptions = res.data.data.map(category => ({
            id: category.id,
            name: category.name
          }));
        }
      }).catch(err => {
        console.error('加载行业选项出错：', err);
      });
    },

    handleFilterChange() {
      this.tableData.page = 1;
      this.getMerchantList(1);
    },

    handleSortChange() {
      this.tableData.page = 1;
      this.getMerchantList(1);
    },

    handleTableSort({ column, prop, order }) {
      console.log('表格排序:', { column, prop, order });

      if (prop && order) {
        const sortFieldMap = {
          'name': 'name',
          'addDataTime': 'addDataTime',
          'lastUpdateTime': 'lastUpdateTime',
          'rebateRatio': 'rebateRatio',
          'totalRebate': 'totalRebate'
        };

        if (sortFieldMap[prop]) {
          this.filterForm.sortField = sortFieldMap[prop];
          this.filterForm.sortOrder = order === 'ascending' ? 'asc' : 'desc';
          this.tableData.page = 1;
          this.getMerchantList(1);
        }
      }
    },

    handleSearch() {
      this.tableData.page = 1;
      this.getMerchantList(1);
    },

    resetFilter() {
      this.$refs.filterFormRef.resetFields();
      this.tableData.page = 1;
      this.getMerchantList(1);
    },

    getMerchantList(page = 1) {
      console.log('开始加载商户列表，页码:', page, '筛选条件:', this.filterForm);

      this.tableData.loading = true;
      if (page !== this.tableData.page) {
        this.tableData.page = page;
      }

      const params = {
        page: this.tableData.page,
        pageSize: this.tableData.pageSize,
        moreFields: true
      };

      if (this.filterForm.name) {
        params.name = this.filterForm.name;
      }

      if (this.filterForm.industryCategoryId) {
        params.industryId = this.filterForm.industryCategoryId;
      }

      if (this.filterForm.status) {
        params.status = this.filterForm.status;
      }

      if (this.filterForm.sortField && this.filterForm.sortOrder) {
        params.sortField = this.filterForm.sortField;
        params.sortOrder = this.filterForm.sortOrder;
      }

      console.log('请求参数:', params);

      api.merchant_getMerchantList(params).then(res => {
        console.log('API响应数据:', res.data);

        if (res.data.code === '000') {
          const data = res.data.data;
          const list = data.list || [];
          const totalCount = data.totalCount || 0;

          console.log('解析出的数据:', { list, totalCount });

          this.tableData.list = list;
          this.tableData.totalCount = totalCount;
          this.tableData.page = this.tableData.page;
          this.tableData.loading = false;

          console.log('更新后的tableData:', this.tableData);

          if (this.tableData.list.length === 0 && this.tableData.page > 1) {
            console.log('当前页无数据，返回上一页');
            this.tableData.page -= 1;
            this.getMerchantList(this.tableData.page);
            return;
          }
        } else {
          this.$message.error('获取商户列表失败：' + (res.data.message || '未知错误'));
          this.tableData.loading = false;
        }
      }).catch(err => {
        console.error('获取商户列表出错：', err);
        this.$message.error('获取商户列表失败，请重试');
        this.tableData.loading = false;
      });
    },

    getMerchantCategories() {
      api.merchantCategory_getMerchantCategories({
        countMerchant: false
      }).then(res => {
        console.log('分类API响应:', res.data);
        if (res.data.code === '000') {
          this.categories = res.data.data || [];
          console.log('加载的分类数据:', this.categories.length);
        } else {
          this.$message.error('获取分类失败：' + res.data.message);
        }
      }).catch(err => {
        console.error('获取分类出错：', err);
        this.$message.error('获取分类失败，请重试');
      });
    },

    addMerchant() {
      this.addMerchantDialog.visible = true;
      this.addMerchantDialog.form = {
        name: '',
        productCount: 10,
        image: null,
        address: '',
        longitude: null,
        latitude: null,
        introduction: '',
        rebateRatio: '',
        phones: [],
        mainClerkPhone: '',
        tags: [],
        industry: '',
        categoryId: null
      };
      this.getMerchantCategories();
    },

    confirmAddMerchant() {
      this.$refs.addMerchantForm.validate(valid => {
        if (!valid) return;

        if (!this.addMerchantDialog.form.image) {
          this.$message.error('请选择商户图片');
          return;
        }

        const formData = {
          name: this.addMerchantDialog.form.name,
          productCount: this.addMerchantDialog.form.productCount,
          imageId: this.addMerchantDialog.form.image.id,
          address: this.addMerchantDialog.form.address,
          introduction: this.addMerchantDialog.form.introduction,
          rebateRatio: this.addMerchantDialog.form.rebateRatio,
          phones: this.addMerchantDialog.form.phones.filter(phone => phone),
          mainClerkPhone: this.addMerchantDialog.form.mainClerkPhone,
          tags: this.addMerchantDialog.form.tags.filter(tag => tag),
          industry: this.addMerchantDialog.form.industry,
          categoryId: this.addMerchantDialog.form.categoryId
        };

        console.log('新增商户数据:', formData);

        api.merchant_addMerchant(formData).then(res => {
          if (res.data.code === '000') {
            this.$message.success('新增商户成功');
            this.addMerchantDialog.visible = false;
            this.getMerchantList(this.tableData.page);
          } else {
            this.$message.error('新增商户失败：' + res.data.message);
          }
        }).catch(err => {
          console.error('新增商户出错：', err);
          this.$message.error('新增商户失败，请重试');
        });
      });
    },

    showDetail(row) {
      console.log('查看商户详情:', row);
      this.storeLoading = true;

      api.merchant_getMerchantDetail({ id: row.id }).then(res => {
        console.log('商户详情响应:', res.data);
        if (res.data.code === '000') {
          const data = res.data.data;
          console.log('商户详情数据:', data);

          this.detailDialog.detail = {
            ...data,
            name: data.name || '',
            mainClerkPhone: data.mainClerkPhone || '--',
            clerkCount: data.clerkCount || 0,
            userCount: data.userCount || 0,
            industry: data.industry || '--',
            rebateRatio: data.rebateRatio || 0,
            rebateAmount: data.rebateAmount || 0,
            unionPayAccount: data.unionPayMerchantNo || data.unionPayAccount || '--',
            imageInfoList: data.imageInfoList || []
          };
          this.detailDialog.visible = true;

          this.$nextTick(() => {
            this.fixDialogZIndex();
          });
        } else {
          this.$message.error('获取商户详情失败：' + res.data.message);
        }
      }).catch(err => {
        console.error('获取商户详情出错：', err);
        this.$message.error('获取商户详情失败，请重试');
      }).finally(() => {
        this.storeLoading = false;
      });
    },

    editMerchant() {
      const { id, name, industry, rebateRatio } = this.detailDialog.detail;

      api.merchant_editMerchant({
        id,
        name,
        industry,
        rebateRatio
      }).then(res => {
        if (res.data.code === '000') {
          this.$message.success('修改成功');
          this.detailDialog.visible = false;
          this.getMerchantList(this.tableData.page);
        } else {
          this.$message.error('修改失败：' + res.data.message);
        }
      }).catch(err => {
        console.error('修改商户出错：', err);
        this.$message.error('修改失败，请重试');
      });
    },

    deleteMerchantImageInfo(index) {
      this.$confirm('确定要删除这张图片吗？', '提示', {
        type: 'warning'
      }).then(() => {
        const imageInfo = this.detailDialog.detail.imageInfoList[index];
        if (imageInfo.id) {
          api.merchant_deleteImageInfo({ id: imageInfo.id }).then(res => {
            if (res.data.code === '000') {
              this.detailDialog.detail.imageInfoList.splice(index, 1);
              this.$message.success('删除成功');
            } else {
              this.$message.error('删除失败：' + res.data.message);
            }
          }).catch(err => {
            console.error('删除图片出错：', err);
            this.$message.error('删除失败，请重试');
          });
        } else {
          this.detailDialog.detail.imageInfoList.splice(index, 1);
          this.$message.success('删除成功');
        }
      });
    },

    addMerchantImageInfo() {
      this.imageInfoUploadDialog = {
        visible: true,
        file: null,
        preview: '',
        name: ''
      };

      this.$nextTick(() => {
        this.fixDialogZIndex();
      });
    },

    selectMerchantInfoImageCallback() {
      const file = this.$refs.imageInfoInput.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = e => {
        this.imageInfoUploadDialog.preview = e.target.result;
        this.imageInfoUploadDialog.file = file;
      };
      reader.readAsDataURL(file);
    },

    confirmUploadImageInfo() {
      const file = this.imageInfoUploadDialog.file;
      if (!file) return;

      fileMd5(file).then(contentMd5 => {
        api.merchant_prepareUploadImageInfo({
          merchantId: this.detailDialog.detail.id,
          contentLength: file.size,
          contentMd5: contentMd5,
          name: this.imageInfoUploadDialog.name
        }).then(res => {
          if (res.data.code !== '000') {
            this.$message.error('上传准备失败：' + res.data.message);
            return;
          }

          const data = res.data.data;
          const xhr = new XMLHttpRequest();
          xhr.open(data.method, data.url);

          if (data.headers) {
            Object.keys(data.headers).forEach(key => {
              if (key.toLowerCase() !== 'host') {
                xhr.setRequestHeader(key, data.headers[key]);
              }
            });
          }

          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status === 200 || xhr.status === 203) {
                api.merchant_finishUploadImageInfo({
                  uploadId: data.uploadId
                }).then(res => {
                  if (res.data.code === '000') {
                    this.$message.success('上传成功');
                    this.detailDialog.detail.imageInfoList.push({
                      id: data.uploadId,
                      name: this.imageInfoUploadDialog.name,
                      url: this.imageInfoUploadDialog.preview
                    });
                    this.imageInfoUploadDialog.visible = false;
                  } else {
                    this.$message.error('上传完成失败：' + res.data.message);
                  }
                });
              } else {
                this.$message.error('上传失败：HTTP ' + xhr.status);
              }
            }
          };

          xhr.send(file);
        });
      });
    },

    selectImage(mode) {
      this.imageSelector.mode = mode;
      this.imageSelector.maxCount = 1;

      this.imageSelector.visible = false;
      this.$nextTick(() => {
        setTimeout(() => {
          this.imageSelector.visible = true;
          this.$nextTick(() => {
            this.fixDialogZIndex();
          });
        }, 50);
      });
    },

    selectImageCallback(images) {
      if (this.imageSelector.mode === 'add' && images.length) {
        this.addMerchantDialog.form.image = images[0];
      }
      this.imageSelector.visible = false;
    },

    handleStoreImageSelect(images) {
      if (images && images.length) {
        this.storeForm.imageId = images[0].id;
        this.storeMainImageUrl = images[0].url || images[0].filepath || '';
      }
      this.storeImageSelector.visible = false;
    },

    profileEdit(row) {
      console.log('编辑店铺，商户ID:', row.id);
      this.storeDialogVisible = true;
      this.storeLoading = true;

      this.storeForm = {
        id: row.id,
        name: '',
        imageId: null,
        status: 1,
        industryCategoryId: null,
        storeCategoryId: null,
        productCategoryId: null,
        businessHours: '',
        contactType: 1,
        contactPhone: '',
        avgPrice: 0,
        introduction: '',
        notice: '',
        address: '',
        longitude: null,
        latitude: null
      };

      this.storeMainImageUrl = '';
      this.contactPhoneError = '';

      this.loadStoreCategories().then(() => {
        this.loadStoreData(row.id).finally(() => {
          this.storeLoading = false;

          this.$nextTick(() => {
            setTimeout(() => {
              this.fixDialogZIndex();
            }, 100);
          });
        });
      });
    },

    delProfile(row){
      console.log('编辑店铺，商户ID:', row.id);

      this.$confirm(`确定要删除商户 "${row.name}" 吗？此操作不可恢复！`, '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning',
        dangerouslyUseHTMLString: true
      }).then(() => {
        // 显示加载状态
        this.tableData.loading = true;

        api.merchant_deleteMerchant({ id: row.id }).then(res => {
          console.log('删除商户响应:', res.data);
          if (res.data.code === '000') {
            this.$message.success(`商户 "${row.name}" 删除成功`);
            // 刷新列表，回到当前页
            this.getMerchantList(this.tableData.page);
          } else {
            this.$message.error('删除失败：' + (res.data.message || '未知错误'));
            this.tableData.loading = false;
          }
        }).catch(err => {
          console.error('删除商户出错：', err);
          this.$message.error('删除失败，请重试');
          this.tableData.loading = false;
        });
      }).catch(() => {
        // 用户取消删除
        this.$message.info('已取消删除');
      });
    },

    loadStoreCategories() {
      this.storeCategoryLoading = true;
      return new Promise((resolve) => {
        api.merchantCategory_getMerchantCategories({
          countMerchant: false
        }).then(res => {
          console.log('店铺分类响应:', res.data);
          if (res.data.code === '000') {
            this.storeCategoryTree = res.data.data || [];
            console.log('店铺分类树:', this.storeCategoryTree);

            this.storeIndustryOptions = this.storeCategoryTree.map(item => ({
              id: item.id,
              name: item.name
            }));

            console.log('一级分类选项:', this.storeIndustryOptions);
          } else {
            this.$message.error('加载分类失败：' + res.data.message);
          }
          resolve();
        }).catch(err => {
          console.error('加载分类出错：', err);
          this.$message.error('加载分类失败，请重试');
          resolve();
        }).finally(() => {
          this.storeCategoryLoading = false;
        });
      });
    },

    loadStoreData(merchantId) {
      return new Promise((resolve) => {
        api.merchant_getMerchantDetail({ id: merchantId }).then(res => {
          console.log('店铺详情API响应:', res.data);
          if (res.data.code === '000') {
            const data = res.data.data;
            console.log('店铺详情数据:', data);

            let avgPrice = 0;
            if (data.avgPriceYuan !== undefined && data.avgPriceYuan !== null) {
              avgPrice = Number(data.avgPriceYuan) || 0;
            } else if (data.avgPrice !== undefined && data.avgPrice !== null) {
              avgPrice = Number(data.avgPrice) / 100;
            }

            this.storeForm = {
              id: data.id,
              name: data.name || '',
              imageId: data.imageId || null,
              status: data.status || 1,
              industryCategoryId: data.industryId || data.categoryId || null,
              storeCategoryId: data.categoryId || null,
              productCategoryId: data.productCategoryId || null,
              businessHours: data.businessHours || '',
              contactType: data.contactType || 1,
              contactPhone: data.contactPhone || data.mainClerkPhone || '',
              avgPrice: avgPrice,
              introduction: data.introduction || '',
              notice: data.notice || '',
              address: data.address || '',
              longitude: data.longitude || null,
              latitude: data.latitude || null
            };

            console.log('填充后的店铺表单:', this.storeForm);

            if (this.storeForm.imageId) {
              this.loadStoreMainImage(this.storeForm.imageId);
            } else if (data.image) {
              this.storeMainImageUrl = data.image;
            }

            if (this.storeForm.industryCategoryId) {
              console.log('有行业分类ID:', this.storeForm.industryCategoryId);
              this.handleIndustryChange(this.storeForm.industryCategoryId, true);

              setTimeout(() => {
                if (this.storeForm.storeCategoryId) {
                  console.log('有店铺分类ID:', this.storeForm.storeCategoryId);
                  this.handleCategoryChange(this.storeForm.storeCategoryId, true);
                }
              }, 100);
            }

            if (this.storeForm.longitude && this.storeForm.latitude) {
              const tencentCoords = this.convertToTencentCoordinates(
                  this.storeForm.longitude,
                  this.storeForm.latitude
              );
              console.log('存储的高德坐标:', this.storeForm.longitude, this.storeForm.latitude);
              console.log('转换的腾讯坐标:', tencentCoords.lng, tencentCoords.lat);
            }
          } else {
            this.$message.error('加载店铺数据失败：' + res.data.message);
          }
          resolve();
        }).catch(err => {
          console.error('加载店铺数据出错：', err);
          this.$message.error('加载店铺数据失败，请重试');
          resolve();
        });
      });
    },

    loadStoreMainImage(imageId) {
      if (!imageId) return;

      api.image_getImageById({ id: imageId }).then(res => {
        if (res.data.code === '000') {
          this.storeMainImageUrl = res.data.data.url || res.data.data.filepath || '';
        }
      }).catch(err => {
        console.error('加载图片出错：', err);
        const imageFromList = this.tableData.list.find(item => item.id === this.storeForm.id);
        if (imageFromList && imageFromList.image) {
          this.storeMainImageUrl = imageFromList.image;
        }
      });
    },

    handleIndustryChange(industryId, isInit = false) {
      console.log('行业分类变化:', industryId, 'isInit:', isInit);

      if (!isInit) {
        this.storeForm.storeCategoryId = null;
        this.storeForm.productCategoryId = null;
        this.storeProductCategoryOptions = [];
      }

      if (!industryId) {
        this.storeCategoryOptions = [];
        return;
      }

      const industry = this.storeCategoryTree.find(item => item.id === industryId);
      console.log('找到的一级分类:', industry);

      if (industry && industry.children) {
        this.storeCategoryOptions = industry.children.map(item => ({
          id: item.id,
          name: item.name
        }));

        console.log('二级分类选项:', this.storeCategoryOptions);

        if (isInit && this.storeForm.storeCategoryId) {
          const hasCategory = this.storeCategoryOptions.some(item => item.id === this.storeForm.storeCategoryId);
          if (!hasCategory) {
            console.log('二级分类ID不存在，重置:', this.storeForm.storeCategoryId);
            this.storeForm.storeCategoryId = null;
          }
        }
      } else {
        this.storeCategoryOptions = [];
        console.log('未找到二级分类或没有children');
      }
    },

    handleCategoryChange(categoryId, isInit = false) {
      console.log('店铺分类变化:', categoryId, 'isInit:', isInit);

      if (!isInit) {
        this.storeForm.productCategoryId = null;
      }

      if (!categoryId || !this.storeForm.industryCategoryId) {
        this.storeProductCategoryOptions = [];
        return;
      }

      const industry = this.storeCategoryTree.find(item => item.id === this.storeForm.industryCategoryId);
      if (!industry || !industry.children) {
        this.storeProductCategoryOptions = [];
        return;
      }

      const category = industry.children.find(item => item.id === categoryId);
      console.log('找到的二级分类:', category);

      if (category && category.children) {
        this.storeProductCategoryOptions = category.children.map(item => ({
          id: item.id,
          name: item.name
        }));

        console.log('三级分类选项:', this.storeProductCategoryOptions);

        if (isInit && this.storeForm.productCategoryId) {
          const hasProduct = this.storeProductCategoryOptions.some(item => item.id === this.storeForm.productCategoryId);
          if (!hasProduct) {
            console.log('三级分类ID不存在，重置:', this.storeForm.productCategoryId);
            this.storeForm.productCategoryId = null;
          }
        }
      } else {
        this.storeProductCategoryOptions = [];
        console.log('未找到三级分类或没有children');
      }
    },

    validateContactPhone() {
      this.contactPhoneError = '';
      const { contactType, contactPhone } = this.storeForm;

      if (!contactPhone) {
        this.contactPhoneError = '请输入联系方式';
        return false;
      }

      if (contactType === 1) {
        const reg = /^1[3-9]\d{9}$/;
        if (!reg.test(contactPhone)) {
          this.contactPhoneError = '请输入正确的11位手机号';
          return false;
        }
      }

      if (contactType === 2) {
        const reg = /^0\d{2,3}-\d{7,8}(-\d{1,4})?$/;
        if (!reg.test(contactPhone)) {
          this.contactPhoneError = '请输入正确的座机号，格式：010-12345678';
          return false;
        }
      }

      return true;
    },

    removeStoreMainImage() {
      this.$confirm('确定要移除店铺主图吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.storeForm.imageId = null;
        this.storeMainImageUrl = '';
      });
    },

    handleStoreSubmit() {
      console.log('提交店铺修改');

      if (!this.validateContactPhone()) {
        this.$message.error(this.contactPhoneError);
        return;
      }

      if (!this.$refs.storeFormRef) {
        this.$message.error('表单引用不存在');
        return;
      }

      this.$refs.storeFormRef.validate(valid => {
        if (!valid) {
          this.$message.error('请检查表单必填项');
          return;
        }

        if (!this.storeForm.imageId) {
          this.$message.error('请选择店铺主图');
          return;
        }

        this.storeSubmitting = true;

        if (this.storeForm.longitude && this.storeForm.latitude) {
          const tencentCoords = this.convertToTencentCoordinates(
              this.storeForm.longitude,
              this.storeForm.latitude
          );

          console.log('提交的坐标:');
          console.log('高德坐标:', this.storeForm.longitude, this.storeForm.latitude);
          console.log('腾讯坐标:', tencentCoords.lng, tencentCoords.lat);
        }

        const submitData = {
          ...this.storeForm,
          avgPrice: Math.round(this.storeForm.avgPrice),
          industryId: this.storeForm.industryCategoryId,
          categoryId: this.storeForm.storeCategoryId
        };

        delete submitData.industryCategoryId;
        delete submitData.storeCategoryId;

        console.log('提交店铺修改数据:', submitData);

        api.merchant_editMerchant(submitData).then(res => {
          console.log('编辑店铺响应:', res.data);
          if (res.data.code === '000') {
            this.$message.success('店铺信息修改成功');
            this.storeDialogVisible = false;
            this.getMerchantList(this.tableData.page);
          } else {
            this.$message.error('修改失败：' + res.data.message);
          }
        }).catch(err => {
          console.error('修改店铺出错：', err);
          this.$message.error('修改失败，请重试');
        }).finally(() => {
          this.storeSubmitting = false;
        });
      });
    },

    toClerks(merchant) {
      this.$router.push({
        path: '/Index/Store/ClerkList',
        query: { merchantId: merchant.id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.merchant-management-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.filter-area {
  margin-bottom: 20px;

  .filter-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);

    :deep(.el-form-item) {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }
}

.merchant-image-infos {
  display: flex;
  flex-wrap: wrap;

  .merchant-image-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 20px;
    margin-bottom: 20px;

    .merchant-image-info-name {
      margin-top: 8px;
      font-size: 12px;
      color: #666;
      text-align: center;
      width: 80px;
      word-break: break-all;
    }
  }
}

.store-edit-form {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;

  .form-section {
    margin-bottom: 30px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;

    .section-title {
      margin: 0 0 20px 0;
      font-size: 16px;
      color: #333;
      font-weight: 600;

      i {
        color: #409eff;
        margin-right: 8px;
      }
    }

    .category-row {
      margin-bottom: 10px;
    }

    .image-upload-container {
      display: flex;
      flex-direction: column;

      .image-preview {
        width: 200px;
        height: 120px;
        border: 1px dashed #dcdfe6;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        overflow: hidden;

        &:hover {
          border-color: #409eff;
        }

        .image-placeholder-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #999;

          i {
            font-size: 24px;
            margin-bottom: 8px;
          }
        }
      }

      .image-actions {
        margin-top: 10px;

        .el-button {
          padding: 0;
        }
      }
    }

    .error-tip {
      font-size: 12px;
      color: #f56c6c;
      margin-top: 4px;
    }
  }
}

.coordinate-info {
  font-size: 12px;
  color: #666;

  .coordinate-item {
    margin-bottom: 4px;

    .coordinate-label {
      display: inline-block;
      width: 120px;
      color: #909399;
      font-weight: 500;
    }

    .coordinate-value {
      font-family: 'Courier New', monospace;
      color: #333;
      background: #f5f7fa;
      padding: 2px 6px;
      border-radius: 3px;
      border: 1px solid #e4e7ed;
    }
  }
}

.merchant-name-cell {
  display: flex;
  align-items: center;

  .el-tag {
    height: 20px;
    line-height: 18px;
    font-size: 12px;
  }
}

.highlight-value {
  color: #e6a23c;
  font-weight: 500;
}

:deep(.el-table__header-wrapper) {
  .el-table-column--sortable {
    .cell {
      display: flex;
      align-items: center;

      .caret-wrapper {
        margin-left: 4px;
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

@media (max-width: 1200px) {
  .filter-area {
    :deep(.el-form-item) {
      margin-bottom: 12px;
      margin-right: 12px;
    }
  }
}

@media (max-width: 768px) {
  .filter-area {
    :deep(.el-form-item) {
      width: 100%;
      margin-right: 0;

      .el-form-item__content {
        width: 100%;

        .el-input,
        .el-select {
          width: 100%;
        }
      }
    }
  }

  .store-edit-form {
    .form-section {
      padding: 15px;

      .category-row {
        .el-col {
          margin-bottom: 10px;
        }
      }
    }
  }
}
</style>

<style>
/* 全局弹窗层级修复 - 确保遮罩层在正确的位置 */
/* 遮罩层应该比对应的弹窗低1，但比其他弹窗高 */
.v-modal {
  z-index: 1999 !important;
}

/* 地图选择器最高优先级 */
.location-selector-dialog .el-dialog__wrapper {
  z-index: 4000 !important;
}

.location-selector-dialog .el-dialog {
  z-index: 4001 !important;
}

.location-selector-dialog .v-modal {
  z-index: 3999 !important;
}

/* 图片选择器第二优先级 */
.image-selector-dialog .el-dialog__wrapper {
  z-index: 3000 !important;
}

.image-selector-dialog .el-dialog {
  z-index: 3001 !important;
}

.image-selector-dialog .v-modal {
  z-index: 2999 !important;
}

/* 店铺编辑弹窗第三优先级 */
.store-edit-dialog .el-dialog__wrapper {
  z-index: 2000 !important;
}

.store-edit-dialog .el-dialog {
  z-index: 2001 !important;
}

.store-edit-dialog .v-modal {
  z-index: 1999 !important;
}

/* 其他弹窗默认层级 */
.merchant-detail-dialog .el-dialog__wrapper,
.add-merchant-dialog .el-dialog__wrapper,
.image-upload-dialog .el-dialog__wrapper {
  z-index: 1000 !important;
}

.merchant-detail-dialog .el-dialog,
.add-merchant-dialog .el-dialog,
.image-upload-dialog .el-dialog {
  z-index: 1001 !important;
}

/* 弹窗遮罩层关系修复 */
.el-dialog__wrapper .v-modal {
  position: fixed !important;
  z-index: inherit;
}

/* 确保弹窗内容在遮罩层之上 */
.el-dialog {
  position: relative;
  z-index: inherit;
}

/* 多重弹窗时，每个弹窗的遮罩层独立 */
.el-dialog__wrapper:nth-child(1) .v-modal {
  z-index: 1999 !important;
}

.el-dialog__wrapper:nth-child(2) .v-modal {
  z-index: 2999 !important;
}

.el-dialog__wrapper:nth-child(3) .v-modal {
  z-index: 3999 !important;
}

/* 店铺编辑弹窗特别处理 - 确保不被遮罩层挡住 */
.store-edit-dialog {
  position: relative;
}

.store-edit-dialog .el-dialog {
  position: relative !important;
}

/* 修复弹窗堆叠问题 */
.el-popup-parent--hidden {
  overflow: hidden !important;
}

/* 确保地图容器可见 */
.map-container,
.amap-container {
  position: relative !important;
  z-index: 10002 !important;
}
</style>